import { ReactNode, ComponentProps } from 'react';

import { IconButton, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

import {
  Divider,
  Icons,
  LabelChip,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { useClusters } from '@cast/shared-cluster-context-data-access';
import { K8sProvider } from '@cast/shared-types';

import { useIsOpDemoMode } from '@cast/infrastructure-demo';
import { LogoFullSvg } from 'assets/svg';
import { Link } from 'components/router';
import { ACTIVITY_PATHS, PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { EXTERNAL_LINKS } from '@cast/constants';
import { useAbility } from '@cast/shared-ability-data-access';
import {
  RADAR_NAV_TITLE,
  useShouldLimitConsoleForReporting,
} from '@cast/shared-trial-ui';
import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';
import { useIsOrganizationAuditLogVisible } from 'hooks/permissions/useIsOrganizationAuditLogVisible';
import { useIsOrganizationReadOnly } from 'hooks/useIsOrganizationReadOnly';
import { HEADER_HEIGHT, PRODUCT_PICKER_WIDTH } from 'main-layout-op/_constants';
import { useMainLayout } from 'main-layout-op/_providers/MainLayoutProvider';
import { Product } from '@cast/console-types';

type ProductItem = {
  product: Product;
  icon: ReactNode;
  title: string;
  subtitle: string;
  badge?: ReactNode;
  supportsDemo?: boolean;
  externalUrl?: string;
  /** Override the navigation destination (defaults to PRODUCT_BASE_PATHS[product]) */
  linkTo?: string;
  /** Additional path prefixes to consider "active" */
  activePathPrefixes?: string[];
};

const MAIN_PRODUCT_ITEMS: ProductItem[] = [
  {
    product: 'automation',
    icon: (
      <Icons.CastCostOptimization variant="preset" weight="light" size={24} />
    ),
    title: 'Automation',
    subtitle: 'Optimize infrastructure',
  },
  {
    product: 'security',
    icon: <Icons.CastSecurity variant="preset" weight="light" size={24} />,
    title: 'Security',
    subtitle: 'Scan & remediate',
  },
  {
    product: 'dbo',
    icon: (
      <Icons.CastDatabaseOptimization
        variant="preset"
        weight="light"
        size={24}
      />
    ),
    title: 'Database',
    subtitle: 'Monitor & optimize',
  },
  {
    product: 'apa',
    icon: <Icons.CastApa variant="preset" weight="light" size={24} />,
    title: 'APA',
    subtitle: 'Agentic runbooks',
    badge: (
      <LabelChip
        size="xs"
        sx={{ backgroundColor: 'indigo.300', color: 'white' }}
      >
        New
      </LabelChip>
    ),
  },
  {
    product: 'llm',
    icon: <Icons.CastKimchi variant="preset" weight="light" size={24} />,
    title: 'AI Enabler → Kimchi',
    subtitle: 'AI coding platform',
    externalUrl: EXTERNAL_LINKS.aie_to_kimchi,
    badge: <Icons.ArrowSquareOut size={14} />,
  },
];

const SETTINGS_ITEM: ProductItem = {
  product: 'settings',
  icon: <Icons.GearSix size={24} weight="light" />,
  title: 'Settings',
  subtitle: 'Organization settings',
};

const AUDIT_LOG_ITEM: ProductItem = {
  product: 'auditLog',
  icon: <Icons.SquareHalf size={24} weight="light" />,
  title: 'Activity log',
  subtitle: 'Across all platform',
  linkTo: ACTIVITY_PATHS.activityEvents,
  activePathPrefixes: [ACTIVITY_PATHS.activity],
  badge: (
    <LabelChip size="xs" sx={{ backgroundColor: 'indigo.300', color: 'white' }}>
      New
    </LabelChip>
  ),
};

const ExternalAnchor = (props: ComponentProps<'a'>) => (
  <a {...props} target="_blank" rel="noopener noreferrer" />
);

type PickerItemProps = ProductItem & {
  onSelect: (product: Product) => void;
};

function PickerItem({
  product,
  icon,
  title,
  subtitle,
  badge,
  externalUrl,
  linkTo,
  activePathPrefixes,
  onSelect,
}: PickerItemProps) {
  const location = useLocation();
  const defaultPath = PRODUCT_BASE_PATHS[product];
  const isActive =
    !externalUrl &&
    (location.pathname.startsWith(defaultPath) ||
      (activePathPrefixes?.some((prefix) =>
        location.pathname.startsWith(prefix)
      ) ??
        false));

  const content = (
    <Stack
      direction="row"
      alignItems="center"
      gap={8}
      p={8}
      sx={{
        borderRadius: '4px',
        bgcolor: isActive ? 'blue.50' : 'transparent',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: isActive ? 'blue.100' : 'grey.100',
        },
      }}
    >
      {icon}
      <Stack>
        <Stack direction="row" alignItems="center" gap={4}>
          <Typography variant="h6" color={isActive ? 'blue.600' : undefined}>
            {title}
          </Typography>
          {badge}
        </Stack>
        <Typography variant="P14M" color="textSecondary">
          {subtitle}
        </Typography>
      </Stack>
    </Stack>
  );

  if (externalUrl) {
    return (
      <ExternalAnchor
        href={externalUrl}
        style={{ textDecoration: 'none', color: 'inherit' }}
        data-testid={`sidebar-item-${product}`}
      >
        {content}
      </ExternalAnchor>
    );
  }

  return (
    <Link
      to={linkTo ?? PRODUCT_BASE_PATHS[product]}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={() => onSelect(product)}
      data-testid={`sidebar-item-${product}`}
    >
      {content}
    </Link>
  );
}

export function ProductPickerSidebar() {
  const { palette } = useTheme();
  const { handleProductSelect, closeProductPicker } = useMainLayout();
  const isOpDemo = useIsOpDemoMode();
  const ability = useAbility();
  const { clusters } = useClusters({ polling: false });

  const anywhereOnly =
    clusters.length > 0 &&
    clusters.every((c) => c.providerType === K8sProvider.ANYWHERE);
  const isCastAnywhereSecurityEnabled = ability.can(
    'view',
    'FeatureSecurityCastAnywhere'
  );
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();

  const isSecurityHidden =
    (anywhereOnly && !isCastAnywhereSecurityEnabled) || isSecuritySunsetEnabled;

  const canViewAuditLog = useIsOrganizationAuditLogVisible();
  const isOrganizationReadOnly = useIsOrganizationReadOnly();
  const limitAutomationConsole = useShouldLimitConsoleForReporting();

  const automationItem: ProductItem = {
    ...MAIN_PRODUCT_ITEMS[0],
    ...(limitAutomationConsole
      ? { title: RADAR_NAV_TITLE, subtitle: 'Reporting' }
      : {}),
  };

  // Reporting trial (non-paid): only Radar domain + Settings.
  const productItems = limitAutomationConsole
    ? [automationItem]
    : (isOpDemo
        ? MAIN_PRODUCT_ITEMS.filter((item) => item.supportsDemo !== false)
        : MAIN_PRODUCT_ITEMS
      ).filter((item) => !(item.product === 'security' && isSecurityHidden));

  const showActivityLog =
    !limitAutomationConsole && canViewAuditLog && !isOrganizationReadOnly;

  return (
    <Stack
      width={PRODUCT_PICKER_WIDTH}
      height="100%"
      bgcolor="grey.50"
      sx={{
        borderRight: '1px solid',
        borderColor: tokens.theme.border.structural.neutral,
        flexShrink: 0,
      }}
      data-testid="product-sidebar"
    >
      <Stack
        height={HEADER_HEIGHT + 4}
        px={12}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexShrink={0}
      >
        <LogoFullSvg aria-label="logo" color={palette.grey[800]} />
        <IconButton size="small" onClick={closeProductPicker}>
          <Icons.X size={16} />
        </IconButton>
      </Stack>
      <Divider />
      <Stack flexGrow={1} overflow="auto" p={12} justifyContent="space-between">
        <Stack gap={8}>
          {productItems.map((item) => (
            <PickerItem
              key={item.product}
              {...item}
              onSelect={handleProductSelect}
            />
          ))}
        </Stack>
        <Stack>
          {showActivityLog && (
            <PickerItem {...AUDIT_LOG_ITEM} onSelect={handleProductSelect} />
          )}
          <PickerItem {...SETTINGS_ITEM} onSelect={handleProductSelect} />
        </Stack>
      </Stack>
    </Stack>
  );
}
